import { useState } from "react";
import type { AvailableSession, BookingFor, OverridableBookingCode, OwnerOverride } from "@teamna/shared";
import type { ErrorCopy } from "@teamna/web-client/api/copy.ts";
import { newIdempotencyKey } from "@teamna/web-client/api/client.ts";
import { useTellClient } from "@teamna/web-client/hooks/use-groups.ts";
import { formatDateTime } from "@teamna/web-client/lib/format.ts";
import { Button, Checkbox, InlineError, TextAreaField, showToast } from "../components/index.ts";

/**
 * A refused add on the owner's session panel, in the owner's words (#ai-feedback 2026-10-26, cards
 * #6 to #8). For a refusal about packages or payment, the owner can:
 *
 * - Add anyway: the client owes one session, and the next package pays it.
 * - Add for free: nothing is owed.
 * - Record a payment: opens the family's page, where the owner records the payment for a package.
 * - Tell the client: a short message the owner can change, into the client's private thread with a
 *   push. Where the club takes card payments, the owner can add the link to buy a package (off by
 *   default).
 *
 * Add anyway shows only to someone who may (`canOverride`). On Desk that is the owner and admins; the
 * staff setting "Staff can add anyway" is for the coach app. Any other refusal shows its words only.
 */
export function AddRefusal({
  businessId,
  session,
  participant,
  refusal,
  canOverride,
  canTell,
  cardPayments,
  bookingFor,
  timezone,
  adding,
  onAdd,
  onRecordPayment,
  onClose,
}: {
  businessId: string;
  session: AvailableSession;
  participant: { id: string; name: string };
  refusal: ErrorCopy & { code: OverridableBookingCode | null; unpaid?: boolean };
  canOverride: boolean;
  /** Tell the client is for the owner and admins (the API refuses others). */
  canTell: boolean;
  cardPayments: boolean;
  bookingFor: BookingFor | undefined;
  timezone: string | undefined;
  /** The add with an override is on its way. */
  adding: OwnerOverride | null;
  onAdd: (override: OwnerOverride) => void;
  /** Opens the family's page, where the owner records a payment. Null when there is no family to open. */
  onRecordPayment: (() => void) | null;
  onClose: () => void;
}) {
  const [telling, setTelling] = useState(false);
  const code = refusal.code;
  return (
    <div className="mt-3 rounded-card border border-line bg-sunken p-3.5" role="alert">
      <p className="text-[14px] font-semibold">{refusal.title}</p>
      <p className="mt-0.5 text-[13px] text-ink-2">{refusal.message}</p>
      {code && !telling ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {canOverride ? (
            <>
              <Button size="sm" loading={adding === "owed"} disabled={adding !== null} onClick={() => onAdd("owed")}>
                Add anyway, owes 1 session
              </Button>
              <Button size="sm" variant="secondary" loading={adding === "free"} disabled={adding !== null} onClick={() => onAdd("free")}>
                Add for free
              </Button>
            </>
          ) : null}
          {onRecordPayment ? (
            <Button size="sm" variant="secondary" disabled={adding !== null} onClick={onRecordPayment}>
              Record a payment
            </Button>
          ) : null}
          {canTell ? (
            <Button size="sm" variant="secondary" disabled={adding !== null} onClick={() => setTelling(true)}>
              Tell the client
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" disabled={adding !== null} onClick={onClose}>
            Cancel
          </Button>
        </div>
      ) : null}
      {code && telling ? (
        <TellClientForm
          businessId={businessId}
          session={session}
          participant={participant}
          code={code}
          unpaid={refusal.unpaid === true}
          cardPayments={cardPayments}
          bookingFor={bookingFor}
          timezone={timezone}
          onDone={onClose}
          onBack={() => setTelling(false)}
        />
      ) : null}
      {!code ? (
        <div className="mt-3">
          <Button size="sm" variant="ghost" onClick={onClose}>
            OK
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function TellClientForm({
  businessId,
  session,
  participant,
  code,
  unpaid,
  cardPayments,
  bookingFor,
  timezone,
  onDone,
  onBack,
}: {
  businessId: string;
  session: AvailableSession;
  participant: { id: string; name: string };
  code: OverridableBookingCode;
  unpaid: boolean;
  cardPayments: boolean;
  bookingFor: BookingFor | undefined;
  timezone: string | undefined;
  onDone: () => void;
  onBack: () => void;
}) {
  const tell = useTellClient(businessId);
  const [text, setText] = useState(() => clientMessage(code, { name: participant.name, self: bookingFor === "self", groupName: session.groupName, when: formatDateTime(session.startsAt, timezone), unpaid }));
  const [payLink, setPayLink] = useState(false);
  // One key for one draft: a second Send after a lost answer replays the first message. A change to
  // the text or the link is a new message.
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);
  return (
    <div className="mt-3 flex flex-col gap-3">
      <TextAreaField
        label="Message to the client"
        hint="It goes to their private messages with you, with a notification on the phone."
        rows={4}
        maxLength={1000}
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          setIdempotencyKey(newIdempotencyKey());
        }}
      />
      {cardPayments ? (
        <Checkbox
          label="Add a link to buy a package"
          checked={payLink}
          onChange={(next) => {
            setPayLink(next);
            setIdempotencyKey(newIdempotencyKey());
          }}
        />
      ) : null}
      {tell.error ? <InlineError error={tell.error} /> : null}
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          loading={tell.isPending}
          disabled={text.trim() === ""}
          onClick={() =>
            tell.mutate(
              { sessionId: session.id, idempotencyKey, participantId: participant.id, text, reason: code, unpaid, payLink },
              {
                onSuccess: () => {
                  showToast(`Message sent to ${participant.name}.`);
                  onDone();
                },
              },
            )
          }
        >
          Send
        </Button>
        <Button size="sm" variant="ghost" disabled={tell.isPending} onClick={onBack}>
          Back
        </Button>
      </div>
    </div>
  );
}

/**
 * The first text of Tell the client, which the owner can change. Where clients book for themselves
 * the client reads "You"; in a family business the parent reads the child's first name.
 */
export function clientMessage(
  code: OverridableBookingCode,
  input: { name: string; self: boolean; groupName: string; when: string; /** The package has sessions but is not paid yet. */ unpaid?: boolean },
): string {
  const first = input.name.split(" ")[0] || input.name;
  const has = input.self ? "You have" : `${first} has`;
  const whose = input.self ? "Your" : `${first}'s`;
  const session = `${input.groupName} on ${input.when}`;
  switch (code) {
    case "GROUP_MISMATCH":
      return `${has} no package for ${input.groupName}. Buy a package for ${input.groupName} to book ${session}.`;
    case "INSUFFICIENT_CREDITS":
      return input.unpaid
        ? `${whose} package is not paid yet. Pay for it to book ${session}.`
        : `${has} no sessions left. Renew the package to book ${session}.`;
    case "PACKAGE_EXPIRED":
      return `${whose} package ends before ${session}. Buy a new package to book it.`;
    case "PACKAGE_PAUSED":
      return `${whose} package is paused on that day, so it cannot pay for ${session}.`;
    case "MEMBERSHIP_CAP_REACHED":
      return `${has} used every session of the membership this month, so it cannot pay for ${session}.`;
    case "MEMBERSHIP_MONTH_UNPAID":
      return `The month of ${session} is not paid yet. Pay it in the app to book the session.`;
  }
}
